import React from 'react';
import { autoId } from '@workday/upc-common';
import { Switch } from '@workday/canvas-kit-react/switch';
import { minusCircleIcon } from '@workday/canvas-system-icons-web';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import {
    ID_ALIAS_NAME,
    ID_CLOSE_BUTTON,
    ID_DONE_BUTTON,
    ID_PRIMARY_ALIAS_SWITCH,
    ID_REMOVE_ALIAS_BUTTON
} from '../../../../autoIds';
import { useAddAliasAsPrimaryMutation, useDeleteAliasMutation } from '../../../../slices/account-api.slice';
import { useTranslation } from 'react-i18next';
import { StatusIndicatorEmphasis, StatusIndicatorType } from '@workday/canvas-kit-react';
import { WclStatusIndicator } from '../../../../shared/indicator/WclStatusIndicator';

export function EditAliasesModalContent({ onClose, emailAliases }) {
    const multipleAliases = emailAliases.length > 1;

    const { t } = useTranslation('account');

    const [deleteAlias] = useDeleteAliasMutation();
    const [addAliasAsPrimary] = useAddAliasAsPrimaryMutation();

    const makePrimary = alias => {
        const payload = {
            alias: alias.alias,
            type: alias.type,
            primary: true
        };
        addAliasAsPrimary(payload);
    };

    const isRemoving = alias => !!emailAliases.find(row => row.alias === alias.alias && row.removing === true);

    return (
        <div className='settingsModalContentWide'>
            <form>
                <h1>{t('EditAliasesModalContent.title')}</h1>
                <h2>{t('EditAliasesModalContent.description')}</h2>

                <div className='settingsModalContent__body'>
                    <table className='manageTable'>
                        <thead>
                            <tr>
                                <th>{t('EditAliasesModalContent.emailColumnTitle')}</th>
                                <th>{t('ContactInfoSettingsRow.primaryLabel')}</th>
                                {multipleAliases && <th>{t('EditAliasesModalContent.remove')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {emailAliases.map(alias => (
                                <tr key={alias.alias}>
                                    <td>
                                        <span {...autoId(ID_ALIAS_NAME)}>{alias.alias}</span>
                                        <WclStatusIndicator
                                            className='verifiedLabel'
                                            type={StatusIndicatorType.Green}
                                            label={t('ContactInfoSettingsRow.verifiedLabel')}
                                            emphasis={StatusIndicatorEmphasis.Low}
                                        />
                                    </td>
                                    <td>
                                        <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                            <Switch
                                                checked={alias.primary}
                                                disabled={alias.primary}
                                                {...autoId(ID_PRIMARY_ALIAS_SWITCH)}
                                                onChange={() => makePrimary(alias)}
                                            />
                                        </div>
                                    </td>
                                    {multipleAliases && (
                                        <td>
                                            {alias.primary ? null : (
                                                <TertiaryButton
                                                    {...autoId(ID_REMOVE_ALIAS_BUTTON)}
                                                    aria-label={t('EditAliasesModalContent.remove')}
                                                    icon={minusCircleIcon}
                                                    size='small'
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        deleteAlias(alias);
                                                    }}
                                                    disabled={isRemoving(alias)}
                                                />
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className='settingsModalContent__footer'>
                    <PrimaryButton
                        {...autoId(ID_DONE_BUTTON)}
                        className='settingsModalContent__submit'
                        size='large'
                        grow={true}
                        onClick={onClose}>
                        {t('EditAliasesModalContent.doneButton')}
                    </PrimaryButton>
                    <TertiaryButton
                        {...autoId(ID_CLOSE_BUTTON)}
                        type='button'
                        className='settingsModalContent__close'
                        size='large'
                        onClick={onClose}
                        grow={true}>
                        {t('EditAliasesModalContent.closeButton')}
                    </TertiaryButton>
                </div>
            </form>
        </div>
    );
}
